import type { AppAction } from '../'

export const enum ErrorState {
  None,
  ConfigLocationUnreachable,
  ConfigUnreachable,
  Unknown,
}

export const enum Stage {
  Pending = 'pending',
  Starting = 'starting',
  Rehydrating = 'rehydrating',
  Configuring = 'configuring',
  Booting = 'booting',
  Initialized = 'initialized',
  Error = 'error',
}

export const enum ActionType {
  Start = 'INIT/START',
  Rehydrate = 'INIT/REHYDRATE',
  RehydrateComplete = 'INIT/REHYDRATE_COMPLETE',
  Configure = 'INIT/CONFIGURE',
  ConfigureComplete = 'INIT/CONFIGURE_COMPLETE',
  Boot = 'INIT/BOOT',
  BootComplete = 'INIT/BOOT_COMPLETE',
  Initialized = 'INIT/INITIALIZED',
  Error = 'INIT/ERROR',
}

export type Action =
  | { type: ActionType.Start }
  | { type: ActionType.Rehydrate }
  | { type: ActionType.RehydrateComplete }
  | { type: ActionType.Configure }
  | { type: ActionType.ConfigureComplete }
  | { type: ActionType.Boot }
  | { type: ActionType.BootComplete }
  | { type: ActionType.Initialized }
  | { type: ActionType.Error; errorState: ErrorState }

export interface State {
  hasBooted: boolean // Used to handle reconfiguration after an initial boot.
  errorState: ErrorState // Used to handle errors that stages cannot reconcile.
  stage: Stage
}

export const initialState: State = {
  hasBooted: false,
  errorState: ErrorState.None,
  stage: Stage.Pending,
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.Start:
      return { ...state, stage: Stage.Starting, errorState: ErrorState.None }

    case ActionType.Rehydrate:
      return { ...state, stage: Stage.Rehydrating }

    case ActionType.Configure:
      return { ...state, stage: Stage.Configuring }

    case ActionType.Boot:
      return { ...state, stage: Stage.Booting }

    case ActionType.BootComplete:
      return { ...state, stage: Stage.Booting, hasBooted: true }

    case ActionType.Initialized:
      return { ...state, stage: Stage.Initialized }

    case ActionType.Error:
      return { ...state, stage: Stage.Error, errorState: action.errorState }

    default:
      return state
  }
}

export const actions = {
  start(): Action {
    return { type: ActionType.Start }
  },
  rehydrate(): Action {
    return { type: ActionType.Rehydrate }
  },
  rehydrateComplete(): Action {
    return { type: ActionType.RehydrateComplete }
  },
  configure(): Action {
    return { type: ActionType.Configure }
  },
  configureComplete(): Action {
    return { type: ActionType.ConfigureComplete }
  },
  boot(): Action {
    return { type: ActionType.Boot }
  },
  bootComplete(): Action {
    return { type: ActionType.BootComplete }
  },
  initialized(): Action {
    return { type: ActionType.Initialized }
  },
  error(errorState: ErrorState): Action {
    return { type: ActionType.Error, errorState }
  },
}

export const queries = {}
